import { Component, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { RevealDirective } from '../../directives/reveal.directive';
import {
  SAAS_BASE_PLAN,
  SAAS_USERS,
  CORE_CATEGORY_RE,
  VERTICAL_CATEGORY_RE,
  MODULE_PRICE,
} from '../../services/saas-pricing';
import { CmsProductItem } from '../../services/cms-data.service';
import { SaasProductsService } from '../../services/saas-products.service';
import { LanguageService } from '../../services/language.service';

interface BasePack {
  name: string;
  price: number;
  annual: number;
  tagline: string;
  features: string[];
}

interface CatalogItem {
  name: string;
  desc: string;
  price: string;
  unit: string;
  color: string;
  /** Complemento consumible: "Incluye N {unidad}" (capacidad). */
  capacity: string | null;
}

interface Pack {
  name: string;
  desc: string;
  price: string;
  unit: string;
  color: string;
}

interface UserType {
  tipo: string;
  desc: string;
  price: string;
}

interface Faq {
  q: string;
  a: string;
}

// Reparto del catálogo CMS: Núcleo = base incluida · Vertical = packs de sector
// · el resto se separa por PRECIO (>= MODULE_PRICE → módulo; por debajo →
// complemento). Ver saas-pricing.MODULE_PRICE.

@Component({
  selector: 'app-precios',
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonModule, RevealDirective],
  templateUrl: './precios.component.html',
  styleUrl: './precios.component.scss',
})
export class PreciosComponent {
  private saas = inject(SaasProductsService);
  private langService = inject(LanguageService);

  // Pack base de pago: el "suelo" que paga todo cliente (brief §4). Precio y
  // usuarios son decisiones de negocio (constantes), no catálogo del CMS.
  base: BasePack = {
    name: SAAS_BASE_PLAN.name,
    price: SAAS_BASE_PLAN.monthly,
    annual: SAAS_BASE_PLAN.monthly * SAAS_BASE_PLAN.annualMonths,
    tagline: 'Tu equipo ficha y tú facturas, todo conectado y en regla.',
    features: [
      'Fichaje del equipo, con las personas que sean',
      'Facturación y presupuestos',
      'Clientes',
      'Registro de gastos',
      'Calendario de fechas clave: plazos fiscales y de cumplimiento',
      'Usuarios operativos ilimitados + 1 usuario completo',
      'Sin permanencia',
    ],
  };

  // Frontera operativo/completo: es de PERMISOS, no de precio (brief §4).
  usuarios: UserType[] = [
    {
      tipo: 'Usuario operativo',
      desc: 'Ficha y opera en su día a día: partes, comandas, tareas. Sin acceso a la gestión.',
      price: 'Ilimitados · incluidos',
    },
    {
      tipo: 'Usuario completo',
      desc: 'Gestiona, factura, ve el global y saca informes; además ficha y opera como el resto. 1 incluido en el pack base.',
      price: `+${SAAS_USERS.fullPrice} €/mes`,
    },
  ];

  // Catálogo servido desde el CMS del SaaS (SaasProductsService).
  isLoading = signal(true);
  modulos = signal<CatalogItem[]>([]);
  complementos = signal<CatalogItem[]>([]);
  packs = signal<Pack[]>([]);

  faqs: Faq[] = [
    {
      q: '¿Qué incluye el pack base?',
      a: 'El fichaje de tu equipo, la facturación con presupuestos, tus clientes, el registro de gastos y un calendario con las fechas clave (plazos fiscales y de cumplimiento). Con usuarios operativos ilimitados y 1 usuario completo. Es la base sobre la que añades solo los módulos que necesites.',
    },
    {
      q: '¿Los usuarios operativos son de verdad ilimitados?',
      a: 'Sí. Da igual que fichen 5 o 40 personas: el precio no cambia. No cobramos licencia por el trabajador que solo ficha; solo pagas por los usuarios completos, los que gestionan, facturan y sacan informes.',
    },
    {
      q: '¿Qué diferencia hay entre módulo y complemento?',
      a: 'Un módulo funciona por sí solo (TPV, Proyectos, Partes de trabajo…) y cuesta 8 €/mes. Un complemento mejora un módulo concreto y no existe sin él (el escaneo IA sobre la facturación, la firma digital sobre los partes): 3 €/mes, precio único.',
    },
    {
      q: '¿Cómo se cobran los módulos extra?',
      a: 'Eliges los módulos que quieras y se suman al pack base en un único pago mensual. Sin facturas sueltas por cada módulo.',
    },
    {
      q: '¿Hay permanencia?',
      a: 'No. Puedes dar de alta o de baja módulos y usuarios cuando quieras.',
    },
    {
      q: '¿Y si necesito algo que no existe?',
      a: 'Lo desarrollamos a medida sobre la misma plataforma, con el acompañamiento de nuestros consultores. Todo unificado, sin duplicar tus datos.',
    },
  ];

  private readonly palette = [
    'var(--ts-mod-plataforma)',
    'var(--ts-mod-facturacion)',
    'var(--ts-mod-crm)',
    'var(--ts-mod-proyectos)',
    'var(--ts-mod-gastos)',
    'var(--ts-mod-fichaje)',
    'var(--ts-mod-tpv)',
    'var(--ts-mod-normativa)',
  ];

  private readonly colorByKeyword: [RegExp, string][] = [
    [/factur|verifactu/i, 'var(--ts-mod-facturacion)'],
    [/cliente|crm/i, 'var(--ts-mod-crm)'],
    [/proyecto|tarea|kanban/i, 'var(--ts-mod-proyectos)'],
    [/gasto|ia|escaneo|ocr/i, 'var(--ts-mod-gastos)'],
    [/fichaj|rrhh|turno|emplea/i, 'var(--ts-mod-fichaje)'],
    [/tpv|comand|restau|venta|inventario|producto|stock/i, 'var(--ts-mod-tpv)'],
    [/normativa|ticketbai|fiscal|calendario|seguro/i, 'var(--ts-mod-normativa)'],
    [/plataforma|documento|archivo|contenido|cms|web|usuario|portal|trastero/i, 'var(--ts-mod-plataforma)'],
  ];

  activeFaq = -1;

  toggleFaq(index: number): void {
    this.activeFaq = this.activeFaq === index ? -1 : index;
  }

  constructor() {
    effect(() => {
      const lang = this.langService.currentLang();
      void this.load(lang);
    });
  }

  private async load(lang: string): Promise<void> {
    this.isLoading.set(true);
    const groups = await this.saas.getModulesByCategory(lang);

    const mods: CatalogItem[] = [];
    const comps: CatalogItem[] = [];
    const verticals: Pack[] = [];
    let i = 0;

    for (const group of groups) {
      if (CORE_CATEGORY_RE.test(group.category)) {
        i += group.items.length; // Núcleo va en la tarjeta base (features).
        continue;
      }
      if (VERTICAL_CATEGORY_RE.test(group.category)) {
        for (const item of group.items) {
          verticals.push({
            name: item.title,
            desc: item.subtitle,
            price: item.price,
            unit: item.priceUnit || '€/mes',
            color: this.colorFor(item, i++),
          });
        }
        continue;
      }
      // Resto: por precio → módulo (>= 8) o complemento (por debajo).
      for (const item of group.items) {
        const priceNum = parseFloat(item.price) || 0;
        if (priceNum >= MODULE_PRICE) mods.push(this.toCard(item, i++));
        else if (priceNum > 0) comps.push(this.toCard(item, i++));
      }
    }

    this.modulos.set(mods);
    this.complementos.set(comps);
    this.packs.set(verticals);
    this.isLoading.set(false);
  }

  private toCard(item: CmsProductItem, index: number): CatalogItem {
    return {
      name: item.title,
      desc: item.subtitle,
      price: item.price,
      unit: item.priceUnit || '€/mes',
      color: this.colorFor(item, index),
      capacity:
        item.unitsIncluded && item.unitsIncluded > 0
          ? `Incluye ${item.unitsIncluded} ${item.unitLabel || 'usos al mes'}`
          : null,
    };
  }

  private colorFor(item: CmsProductItem, index: number): string {
    const haystack = `${item.title} ${item.category}`;
    for (const [re, color] of this.colorByKeyword) {
      if (re.test(haystack)) return color;
    }
    return this.palette[index % this.palette.length];
  }
}
